import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonTextInput } from 'views/components/base/inputs/TextInput';
import { CommonFileInput } from 'views/components/base/inputs/FileInput';
import { Video } from 'constants/index';
import { mediaFileName, resolveMediaUrl } from 'utils/media/resolveMediaUrl';
import { readVideoDurationInMinutes } from 'utils/media/videoDuration';
import { hasAcceptedFormat } from 'utils/file/fileValidation';
import { getUploadErrorMessage, uploadVideoFiles, VideoUploadFiles } from 'services/video/videoService';
import type { VideoRow } from './createVideoForm';
import './videoFormComponent.scss';

const VIDEO_ACCEPT = 'video/mp4,video/webm,.mp4,.webm';
const VIDEO_FORMATS = ['mp4', 'webm'];
const CAPTIONS_ACCEPT = '.vtt,.srt,text/vtt';
const CAPTIONS_FORMATS = ['vtt', 'srt'];
const FULL_PROGRESS = 100;

interface VideoFormProps {
  isEdit: boolean | undefined;
  videoData: VideoRow;
  editRow: (rowId: number, field: keyof Video, newValue: string | number) => void;
  removeRow: (rowId: number) => void;
  onUploadStart: () => void;
  onUploadEnd: () => void;
}

export function VideoFormComponent({
  isEdit,
  videoData,
  editRow,
  removeRow,
  onUploadStart,
  onUploadEnd
}: Readonly<VideoFormProps>) {
  const { t } = useTranslation(['videoForm']);
  const { rowId } = videoData;

  const [videoFile, setVideoFile] = useState<File | undefined>(undefined);
  const [captionsFile, setCaptionsFile] = useState<File | undefined>(undefined);
  const [videoProgress, setVideoProgress] = useState<number | undefined>(undefined);
  const [captionsProgress, setCaptionsProgress] = useState<number | undefined>(undefined);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | undefined>(undefined);

  const videoName = mediaFileName(videoData.url);
  const captionsName = mediaFileName(videoData.captionsUrl);
  const captionsHref = resolveMediaUrl(videoData.captionsUrl);

  const upload = async (files: VideoUploadFiles, setProgress: (percent: number | undefined) => void) => {
    setUploadErrorMessage(undefined);
    setProgress(0);
    onUploadStart();
    try {
      const uploaded = await uploadVideoFiles(files, setProgress);
      if (uploaded.url) {
        editRow(rowId, 'url', uploaded.url);
      }
      if (uploaded.captionsUrl) {
        editRow(rowId, 'captionsUrl', uploaded.captionsUrl);
      }
    } catch (error) {
      setUploadErrorMessage(getUploadErrorMessage(error));
      // dropping the file resets the file input (see CommonFileInput), only for the field that failed
      if (files.video) {
        setVideoFile(undefined);
      }
      if (files.captions) {
        setCaptionsFile(undefined);
      }
    } finally {
      setProgress(undefined);
      onUploadEnd();
    }
  };

  const handleVideoSelected = async (file: File | undefined) => {
    setVideoFile(file);
    if (!file || !hasAcceptedFormat(file, VIDEO_FORMATS)) {
      return;
    }
    // The duration is read in the browser while the upload runs, and prefills the field, which stays editable.
    void readVideoDurationInMinutes(file).then((minutes) => {
      if (minutes) {
        editRow(rowId, 'timeToComplete', minutes);
      }
    });
    await upload({ video: file }, setVideoProgress);
  };

  const handleCaptionsSelected = async (file: File | undefined) => {
    setCaptionsFile(file);
    if (!file || !hasAcceptedFormat(file, CAPTIONS_FORMATS)) {
      return;
    }
    await upload({ captions: file }, setCaptionsProgress);
  };

  return (
    <section className="video-form">
      <div className="video-form__header">
        <h3 className="video-form__title">{t('rowTitle', { order: videoData.videoOrder })}</h3>
        <button type="button" className="video-form__delete" onClick={() => removeRow(rowId)}>
          ✕ {t('delete')}
        </button>
      </div>

      <div className="video-form__grid">
        {/* Video file */}
        <div className="video-form__field">
          <p className="video-form__current">
            {t('currentVideo')} <b>{videoName ?? t('noVideo')}</b>
          </p>
          <CommonFileInput
            isEdit={isEdit}
            file={videoFile}
            placeholder={t('videoPlaceholder')}
            setFileVariable={(file) => {
              void handleVideoSelected(file);
            }}
            errorMessage={t('videoFileError')}
            labelPlaceholder={videoData.url ? t('videoFileReplace') : t('videoFile')}
            labelClasses="video-form__input-label"
            formatMessage={t('videoFormatMessage')}
            acceptedFormats={VIDEO_FORMATS}
            accept={VIDEO_ACCEPT}
            disabled={videoProgress !== undefined}
          />
          {videoProgress !== undefined && (
            <div className="video-form__progress">
              <progress value={videoProgress} max={FULL_PROGRESS} />
              <span>
                {t('uploadingVideo')} {videoProgress}%
              </span>
            </div>
          )}
        </div>

        {/* Captions file */}
        <div className="video-form__field">
          <p className="video-form__current">
            {t('currentCaptions')} <b>{captionsName ?? t('noCaptions')}</b>
          </p>
          {captionsHref && (
            <a className="video-form__captions-link" href={captionsHref} download>
              {t('downloadCaptions')}
            </a>
          )}
          <CommonFileInput
            isEdit={isEdit}
            file={captionsFile}
            placeholder={t('captionsPlaceholder')}
            setFileVariable={(file) => {
              void handleCaptionsSelected(file);
            }}
            errorMessage={t('captionsFileError')}
            labelPlaceholder={videoData.captionsUrl ? t('captionsFileReplace') : t('captionsFile')}
            labelClasses="video-form__input-label"
            formatMessage={t('captionsFormatMessage')}
            acceptedFormats={CAPTIONS_FORMATS}
            accept={CAPTIONS_ACCEPT}
            disabled={captionsProgress !== undefined}
          />
          {captionsProgress !== undefined && (
            <div className="video-form__progress">
              <progress value={captionsProgress} max={FULL_PROGRESS} />
              <span>
                {t('uploadingCaptions')} {captionsProgress}%
              </span>
            </div>
          )}
        </div>

        {/* timeToComplete */}
        <div className="video-form__field">
          <CommonTextInput
            isEdit={isEdit}
            onChange={(value: string) => editRow(rowId, 'timeToComplete', Number(value))}
            existingValue={videoData.timeToComplete.toString()}
            inputPlaceholder={t('timeToComplete')}
            labelPlaceholder={t('timeToComplete')}
            labelClasses="video-form__input-label"
            errorMessage={t('timeToCompleteError')}
            validationRules={{ isRequired: true, isInteger: true, isNumber: true }}
            isVideoLot={true}
          />
        </div>

        {/* Order */}
        <div className="video-form__field">
          <CommonTextInput
            isEdit={isEdit}
            onChange={(value: string) => editRow(rowId, 'videoOrder', Number(value))}
            existingValue={videoData.videoOrder.toString()}
            inputPlaceholder={t('videoOrder')}
            labelPlaceholder={t('videoOrder')}
            labelClasses="video-form__input-label"
            errorMessage={t('videoOrderError')}
            validationRules={{ isRequired: true, isInteger: true, isNumber: true }}
            isVideoLot={true}
          />
        </div>
      </div>

      {uploadErrorMessage && <p className="video-form__error">{uploadErrorMessage}</p>}
    </section>
  );
}
